import React, { useEffect, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  InputBase,
  Popover,
  Radio,
  RadioGroup,
  Theme,
  Typography,
} from '@material-ui/core';
import ToggleButton from '@material-ui/lab/ToggleButton';
import ToggleButtonGroup from '@material-ui/lab/ToggleButtonGroup';
import { createStyles, makeStyles } from '@material-ui/styles';
import { TabletButton } from '@campfire/tablet-button';
import { capitalize } from 'lodash';
import { DataGrid, GridCellParams, GridColumns } from '@material-ui/data-grid';
import {
  ArrowDownward,
  ArrowUpward,
  Build,
  CheckCircle as CheckCircleIcon,
  Close,
  ExpandMore,
  GetApp,
  LocationOn,
  Refresh,
  Search,
  ViewColumn,
  Warning,
} from '@material-ui/icons';
import { DateTime } from 'luxon';
import { EditorState, convertFromRaw } from 'draft-js';
import { withRouter } from 'react-router-dom';
import { useQueryParam } from 'use-query-params';
import { useCampfireTheme } from '../../../../theme/useCampfireTheme';
import { useApiUrl } from '../../../../global/config/useApiUrl';
import { useCampfireFetch } from '../../../../global/network/useCampfireFetch';
import { VolunteerProfile } from '../../../../common/AvatarName';
import './simple-db.css';
import { RoleIcon } from '../../../../common/icons/RoleIconList';
import { TaskBadge } from '../../../admin/tasks/TaskBadge';
import { VolunteerCommonProfileDialog } from '../../volunteer-common-profile/VolunteerCommonProfileDialog';

import { TooltipMenu } from '../../../../common/tooltip-menu';

import { useCampfireQuery } from '../../../../global/network/useCampfireQuery';
import { GET_PROGRAM_ACTIVITIES_VOL_DB } from '../../../../global/programs-sell/program-activites.gql';
import { ExportsList } from '../../../../common/export/ExportsList';
import {
  GetProgramActivitiesVolDB,
  GetProgramActivitiesVolDB_vm_programs_activities as ProgramActivityType,
  GetProgramActivitiesVolDBVariables,
} from '../../../../global/programs-sell/__generated__/GetProgramActivitiesVolDB';
import { useUser } from '../../../../global/auth/useUser';
import { LocationSearchDialog } from './LocationSearchDialog';
import { isDraftJsContent } from '../../../../common/comment/CommentBox';

interface VolunteerDatabaseArray {
  total: number;
  volunteers: GetVolunteerDatabaseVolunteer[];
}

interface RoleType {
  roleId: string;
  roleName: string;
  roleIcon: string | null;
  completed: boolean;
}

interface ActivityType {
  activityId: string;
  name: string;
  programId: string;
  programName: string;
}

interface ProgramType {
  programId: string;
  programName: string;
}

interface GetVolunteerDatabaseVolunteer {
  id: string;
  volunteerId: string;
  profileId: string;
  userId: string;
  avatarUrl: string;
  isDeactivated: boolean;
  isFlagged: boolean;
  firstName: string;
  status: { string: string; date: { date: string } };
  preferredName: string | null;
  lastName: string;
  email: string;
  dob: string | null;
  contactNumber: string | null;
  dateCreated: { date: string };
  commentCount: number;
  roles: RoleType[];
  activities: ActivityType[];
  programs: ProgramType[];
  distance?: number; // Distance in km when location search is active
  description?: string; // Optional: profile description
  volunteerHours?: number; // Optional: total volunteer hours
  lastAttended?: { date: string } | null; // Optional: last attendance date
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    hideRightSeparator: {
      '.MuiDataGrid-columnSeparator': {
        display: 'none !important',
      },
    },
    edit: {
      color: theme.alert.blue.extraLight,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '14px',
      '& span': {
        fontSize: '14px',
      },
      '& p': {
        fontSize: '14px',
      },
      '& svg': {
        fontSize: '16px',
      },
      textTransform: 'none',
    },
    disabledLabel: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '14px',
      '& span': {
        fontSize: '14px',
      },
      '& p': {
        fontSize: '14px',
      },
      '& svg': {
        fontSize: '16px',
      },
      '& .Mui-disabled': {
        color: 'rgba(255, 255, 255, 0.6) !important',
      },
      textTransform: 'none',
      cursor: 'not-allowed',
    },
  })
);

// Helper function to convert Draft.js description to plain text
const descriptionToPlainText = (description: string | null | undefined): string => {
  if (!description) {
    return '';
  }
  try {
    if (isDraftJsContent(description)) {
      return EditorState.createWithContent(convertFromRaw(JSON.parse(description)))
        .getCurrentContent()
        .getPlainText();
    }
    return description;
  } catch (error) {
    // If parsing fails, return the original string or empty
    return description || '';
  }
};

const VolunteerDatabaseScreenSimple = withRouter(() => {
  const classes = useStyles();
  const { theme } = useCampfireTheme();

  const [volunteerDatabase, setVolunteerDatabase] = useState<VolunteerDatabaseArray | undefined>(undefined);
  const [limit, setLimit] = useState<number>(25);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [filterString, setFilterString] = useState<string>('');
  const [profileOpen, setProfileOpen] = useState<boolean>(false);
  const [openProfilePage, setOpenProfilePage] = useState<string | undefined>();
  const [openProfileId, setOpenProfileId] = useState<string | null>(null);
  const [openUserId, setOpenUserId] = useState<string | null>(null);

  const [openVolunteers, setOpenVolunteers] = React.useState<boolean>(false);
  const [openPrograms, setOpenPrograms] = React.useState<boolean>(false);
  const [openActivities, setOpenActivities] = React.useState<boolean>(false);
  const [openRoles, setOpenRoles] = React.useState<boolean>(false);
  const [openTools, setOpenTools] = React.useState<boolean>(false);
  const [openColumns, setOpenColumns] = React.useState<boolean>(false);
  const [exportsPopoverAnchor, setExportsPopoverAnchor] = React.useState<HTMLElement | null>(null);
  const [locationSearchOpen, setLocationSearchOpen] = React.useState<boolean>(false);

  const [includeInactive, setIncludeInactive] = React.useState<string>('active');
  const [programFilter, setProgramFilter] = React.useState<Array<string>>([]);
  const [activityFilter, setActivityFilter] = React.useState<Array<string>>([]);
  const [roleFilter, setRoleFilter] = React.useState<Array<string>>([]);
  const [orderBy, setOrderBy] = React.useState<string>('name');
  const [orderDirection, setOrderDirection] = React.useState<boolean>(true);

  // Location search filters
  const [locationFilter, setLocationFilter] = React.useState<{
    lat: number;
    long: number;
    range: number;
    sort: 'distance' | 'distance_desc' | 'alphabetical';
    addressTaskFieldId: string;
  } | null>(null);
  const [locationChipHovered, setLocationChipHovered] = React.useState(false);

  // Optional columns - all optional columns are disabled by default
  const [optionalColumns, setOptionalColumns] = React.useState<{
    dateOfBirth: boolean;
    description: boolean;
    volunteerHours: boolean;
    lastAttended: boolean;
  }>({
    dateOfBirth: false,
    description: false,
    volunteerHours: false,
    lastAttended: false,
  });

  const apiUrl = useApiUrl();
  const campfireMutation = useCampfireFetch({ defer: true });

  const { data: programActivityData } = useCampfireQuery<GetProgramActivitiesVolDB, GetProgramActivitiesVolDBVariables>(
    GET_PROGRAM_ACTIVITIES_VOL_DB
  );

  const { getManagerIdentity, user } = useUser();
  const { programManagerIdentities } = getManagerIdentity();
  const managedProgramIds = programManagerIdentities.flatMap((p) => p.programId);

  const activityRoles = programActivityData?.vm.activityRoles;

  const filteredProgramIds = user.userIdentityService.isProgramManager
    ? programActivityData?.vm.programs.filter((p) => managedProgramIds.includes(p.programId))
    : programActivityData?.vm.programs;

  const activitiesFilterData = filteredProgramIds?.flatMap((p) =>
    p.activities.flatMap((a) => {
      return a;
    })
  );

  const [activityFilterOptions, setActivityFilterOptions] = React.useState<ProgramActivityType[] | null>(null);

  if (programActivityData && activitiesFilterData && activityFilterOptions === null) {
    setActivityFilterOptions(activitiesFilterData);
  }

  function fetch(newPage?: number) {
    setLoading(true);
    setError(null);
    campfireMutation
      .run({
        url: `${apiUrl}/vm/management/volunteer-database`,
        method: 'post',
        data: {
          filterByString: filterString,
          filterByProgramIds: programFilter,
          filterByActivityIds: activityFilter,
          filterByRoles: roleFilter,
          includeInactive: includeInactive === 'all' || includeInactive === 'inactive',
          onlyInactive: includeInactive === 'inactive',
          orderBy: orderBy,
          orderDirection: orderDirection,
          offset: newPage === undefined ? currentPage * 25 : newPage * 25,
          limit: 25,
          // Location search parameters
          ...(locationFilter && {
            locationLat: locationFilter.lat,
            locationLong: locationFilter.long,
            locationRange: locationFilter.range,
            addressTaskFieldId: locationFilter.addressTaskFieldId,
          }),
          // Optional columns
          optionalColumns: {
            dateOfBirth: optionalColumns.dateOfBirth,
            description: optionalColumns.description,
            volunteerHours: optionalColumns.volunteerHours,
            lastAttended: optionalColumns.lastAttended,
          },
        },
      })
      .then((response) => {
        if (response && response.data && response.data.data) {
          setVolunteerDatabase(response.data.data as VolunteerDatabaseArray);
          setError(null);
        } else {
          setError('Failed to load volunteer database data');
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error('Volunteer database query error:', err);
        setError('Error loading volunteer database. Please try again.');
        setLoading(false);
      });
  }

  function clear() {
    setVolunteerDatabase(undefined);
  }

  function reset() {
    setProgramFilter([]);
    setActivityFilter([]);
    setRoleFilter([]);
    setFilterString('');
    setIncludeInactive('active');
    setLocationFilter(null);
    changePage(0);
    clear();
  }

  useEffect(() => {
    if (!volunteerDatabase) {
      fetch();
    }
    if (activitiesFilterData && programFilter.length > 0) {
      setActivityFilterOptions(activitiesFilterData?.filter((a) => programFilter.includes(a.program.programId)));
    }
  }, [volunteerDatabase]);

  function changePage(page: number) {
    setCurrentPage(page);
    fetch(page);
  }

  function programFilterChange(programId: string) {
    if (programFilter.includes(programId)) {
      setProgramFilter(
        programFilter.filter((a) => {
          return a !== programId;
        })
      );
    } else {
      setProgramFilter([...programFilter, programId]);
    }
    changePage(0);
    clear();
  }

  function activityFilterChange(activityId: string) {
    if (activityFilter.includes(activityId)) {
      setActivityFilter(
        activityFilter.filter((a) => {
          return a !== activityId;
        })
      );
    } else {
      setActivityFilter([...activityFilter, activityId]);
    }
    changePage(0);
    clear();
  }

  function roleFilterChange(roleId: string) {
    if (roleFilter.includes(roleId)) {
      setRoleFilter(
        activityFilter.filter((r) => {
          return r !== roleId;
        })
      );
    } else {
      setRoleFilter([...roleFilter, roleId]);
    }
    changePage(0);
    clear();
  }

  function updateLimit(number: number) {
    setLimit(number);
    clear();
  }

  function handleDownloadButtonClick(event: React.MouseEvent<HTMLElement>) {
    setExportsPopoverAnchor(event.currentTarget);
  }

  function handleExportsPopoverClose() {
    setExportsPopoverAnchor(null);
  }

  function toggleOptionalColumn(columnKey: keyof typeof optionalColumns) {
    setOptionalColumns((prev) => ({
      ...prev,
      [columnKey]: !prev[columnKey],
    }));
    clear(); // Refresh data when columns change
  }

  function handleCsvDownloadRequest() {
    campfireMutation
      .run({
        url: `${apiUrl}/vm/management/volunteer-database`,
        method: 'post',
        data: {
          filterByString: filterString,
          filterByProgramIds: programFilter,
          filterByActivityIds: activityFilter,
          filterByRoles: roleFilter,
          includeInactive: includeInactive === 'all' || includeInactive === 'inactive',
          onlyInactive: includeInactive === 'inactive',
          orderBy: orderBy,
          orderDirection: orderDirection,
          offset: 0,
          limit: 25,
          download: true,
          // Location search parameters for export
          ...(locationFilter && {
            locationLat: locationFilter.lat,
            locationLong: locationFilter.long,
            locationRange: locationFilter.range,
            addressTaskFieldId: locationFilter.addressTaskFieldId,
          }),
          // Optional columns for export
          optionalColumns: {
            dateOfBirth: optionalColumns.dateOfBirth,
            description: optionalColumns.description,
            volunteerHours: optionalColumns.volunteerHours,
            lastAttended: optionalColumns.lastAttended,
          },
        },
      })
      .then((response) => {
        if (response && response.data && response.data.data) {
          // The response should contain an exportId that we can use to track the download
        }
      });
  }

  function profileOpenTrigger(page: string, userId: string, setProfileId: string) {
    setOpenProfilePage(page);
    setOpenProfileId(setProfileId);
    setOpenUserId(userId);
    setProfileOpen(true);
  }

  function handleLocationSearchClick() {
    setOpenTools(false);
    setLocationSearchOpen(true);
  }

  function handleLocationSearch(params: {
    lat: number;
    long: number;
    range: number;
    sort: 'distance' | 'distance_desc' | 'alphabetical';
    addressTaskFieldId: string;
  }) {
    setLocationFilter(params);
    // When location search is active, update the orderBy to reflect the sort preference
    if (params.sort !== 'alphabetical') {
      setOrderBy('distance');
      setOrderDirection(params.sort === 'distance'); // true for closest, false for furthest
    } else {
      setOrderBy('name');
      setOrderDirection(true);
    }
    changePage(0);
    clear();
  }

  function setOrder(type: string) {
    if (!orderDirection && type === orderBy) {
      setOrderDirection(true);
      setOrderBy('name');
    }
    if (type !== orderBy) {
      setOrderDirection(true);
      setOrderBy(type);
    } else {
      setOrderDirection(!orderDirection);
      setOrderBy(type);
    }
    clear();
  }

  const columns: GridColumns = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      disableColumnMenu: true,
      minWidth: 180,
      hideSortIcons: true,
      sortable: false,
      renderHeader: () => {
        return (
          <Box
            onClick={() => {
              setOrder('name');
            }}
            style={{ display: 'flex', alignItems: 'center', gap: '3px' }}
          >
            <Typography style={{ fontWeight: 600, fontSize: '14px' }}>Name</Typography>
            {orderBy === 'name' ? (
              orderDirection ? (
                <ArrowUpward style={{ fontSize: '18px', color: '#464646' }} />
              ) : (
                <ArrowDownward style={{ fontSize: '18px', color: '#464646' }} />
              )
            ) : null}
          </Box>
        );
      },
      renderCell: (params: GridCellParams) => {
        const { row } = params;
        const today = DateTime.local();
        const dateOfBirth = row.dob ? DateTime.fromJSDate(new Date(Date.parse(row.dob))) : null;
        const isBirthday = dateOfBirth ? dateOfBirth.month === today.month && dateOfBirth.day === today.day : false;

        let description = row.isFlagged ? 'Flagged & Exited' : row.isDeactivated ? 'Retired' : 'Volunteer';
        if (isBirthday && !row.isFlagged && !row.isDeactivated) {
          description = 'Volunteer 🎂';
        }

        return (
          <VolunteerProfile
            inTable
            noTrim
            profileId={row.profileId}
            name={`${row.preferredName ?? row.firstName} ${row.lastName}`}
            userId={row.userId}
            lastName={row.lastName}
            preferredName={row.preferredName ?? row.firstName}
            avatarUrl={row.avatarUrl}
            description={description}
          />
        );
      },
    },
    ...(locationFilter
      ? [
          {
            field: 'distance',
            headerName: 'Distance',
            width: 100,
            disableColumnMenu: true,
            hideSortIcons: true,
            sortable: false,
            renderHeader: () => {
              return (
                <Box
                  onClick={() => {
                    setOrder('distance');
                  }}
                  style={{ display: 'flex', alignItems: 'center', gap: '3px' }}
                >
                  <Typography style={{ fontWeight: 600, fontSize: '14px' }}>Distance</Typography>
                  {orderBy === 'distance' ? (
                    orderDirection ? (
                      <ArrowUpward style={{ fontSize: '18px', color: '#464646' }} />
                    ) : (
                      <ArrowDownward style={{ fontSize: '18px', color: '#464646' }} />
                    )
                  ) : null}
                </Box>
              );
            },
            renderCell: (params: GridCellParams) => {
              const { row } = params;
              if (!row.distance) {
                return <Typography style={{ fontSize: '13px' }}>-</Typography>;
              }

              // Distance comes from backend in meters
              const distanceInMeters = row.distance;
              const displayText =
                distanceInMeters > 999
                  ? `${(distanceInMeters / 1000).toFixed(1)} km`
                  : `${Math.round(distanceInMeters)} m`;

              return <Typography style={{ fontSize: '13px' }}>{displayText}</Typography>;
            },
          },
        ]
      : []),
    {
      field: 'email',
      headerName: 'Email',
      flex: 1,
      disableColumnMenu: true,
      hideSortIcons: true,
      sortable: false,
      renderHeader: () => {
        return (
          <Box
            onClick={() => {
              setOrder('email');
            }}
            style={{ display: 'flex', alignItems: 'center', gap: '3px' }}
          >
            <Typography style={{ fontWeight: 600, fontSize: '14px' }}>Email</Typography>
            {orderBy === 'email' ? (
              orderDirection ? (
                <ArrowUpward style={{ fontSize: '18px', color: '#464646' }} />
              ) : (
                <ArrowDownward style={{ fontSize: '18px', color: '#464646' }} />
              )
            ) : null}
          </Box>
        );
      },
    },
    {
      field: 'contactNumber',
      headerName: 'Phone',
      width: 130,
      disableColumnMenu: true,
      hideSortIcons: true,
      sortable: false,
      renderHeader: () => {
        return <Typography style={{ fontWeight: 600, fontSize: '14px' }}>Phone</Typography>;
      },
    },
    {
      field: 'dateCreated',
      headerName: 'Created',
      width: 100,
      disableColumnMenu: true,
      hideSortIcons: true,
      sortable: false,
      renderHeader: () => {
        return (
          <Box
            onClick={() => {
              setOrder('date');
            }}
            style={{ display: 'flex', alignItems: 'center', gap: '3px' }}
          >
            <Typography style={{ fontWeight: 600, fontSize: '14px' }}>Created</Typography>
            {orderBy === 'date' ? (
              orderDirection ? (
                <ArrowUpward style={{ fontSize: '18px', color: '#464646' }} />
              ) : (
                <ArrowDownward style={{ fontSize: '18px', color: '#464646' }} />
              )
            ) : null}
          </Box>
        );
      },
      renderCell: (params: GridCellParams) => {
        const { row } = params;
        const today = DateTime.local();
        const createdDate = DateTime.fromJSDate(new Date(Date.parse(row.created.date)));
        const isAnniversary =
          createdDate.hasSame(today, 'month') &&
          createdDate.hasSame(today, 'day') &&
          !createdDate.hasSame(today, 'year');

        return (
          <Typography style={{ fontSize: '13px' }}>
            {createdDate.toFormat('d MMM yyyy')}
            {isAnniversary && ' 🎉'}
          </Typography>
        );
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      disableColumnMenu: true,
      hideSortIcons: true,
      sortable: false,
      renderHeader: () => {
        return <Typography style={{ fontWeight: 600, fontSize: '14px' }}>Status</Typography>;
      },
      renderCell: (params: GridCellParams) => {
        const { row } = params;
        if (row.status.date === null) {
          return <Typography style={{ fontSize: '13px', color: '#646464' }}>{row.status.string}</Typography>;
        }
        return (
          <Box>
            <Typography style={{ fontSize: '12px', color: '#646464' }}>{row.status.string}</Typography>
            <Typography style={{ fontSize: '13px' }}>
              {DateTime.fromJSDate(new Date(Date.parse(row.status.date.date))).toFormat('d LLLL yyyy')}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'comments',
      headerName: 'Comments',
      width: 60,
      sortable: false,
      disableColumnMenu: true,
      renderHeader: () => {
        return <Typography style={{ fontWeight: 600, fontSize: '14px' }}></Typography>;
      },
      renderCell: (params: GridCellParams) => {
        const { row } = params;
        return (
          <div
            onClick={() => profileOpenTrigger('comments', row.userId, row.profileId)}
            style={{ display: 'contents', cursor: 'pointer' }}
          >
            <Box style={{ display: 'flex', flex: '1', justifyContent: 'center' }}>
              <svg width='19' height='18' viewBox='0 0 19 18' fill='none' xmlns='http://www.w3.org/2000/svg'>
                <path
                  d='M9.17279 0C14.2387 0 18.3456 3.31835 18.3456 7.41177C18.3456 9.72424 17.0339 11.7889 14.9802 13.1479C15.2472 14.3704 15.8694 16.1836 17.3264 18C14.3921 17.4918 12.5158 16.0089 11.3559 14.6033C10.6547 14.742 9.92649 14.8235 9.17279 14.8235C4.10686 14.8235 0 11.5052 0 7.41177C0 3.31835 4.10686 0 9.17279 0Z'
                  fill='#646464'
                />
              </svg>
            </Box>
            {row.commentCount >= 1 && (
              <Box style={{ position: 'absolute', marginLeft: '22px', marginBottom: '12px' }}>
                <TaskBadge number={row.commentCount} variant='warning' size='small' />
              </Box>
            )}
          </div>
        );
      },
    },
    {
      field: 'roles',
      headerName: 'Roles',
      width: 115,
      sortable: false,
      disableColumnMenu: true,
      renderHeader: () => {
        return (
          <Typography style={{ fontWeight: 600, fontSize: '14px', textAlign: 'center', justifySelf: 'center' }}>
            Roles
          </Typography>
        );
      },
      renderCell: ({ row }: any) => {
        const noIcon = row.roles.filter((role: RoleType) => {
          return !role.roleIcon;
        });
        const icons = row.roles.filter((role: RoleType) => {
          return role.roleIcon;
        });

        if (icons.length > 0) {
          return (
            <Box
              style={{ display: 'flex', flex: '1', justifyContent: 'center' }}
              onClick={() => profileOpenTrigger('profile', row.userId, row.profileId)}
            >
              {row.roles.map((role: RoleType) => {
                if (role.roleIcon) {
                  return (
                    <Box
                      marginRight='6px'
                      marginTop='4px'
                      paddingBottom='4px'
                      style={{ cursor: 'pointer' }}
                      // onClick={() => onRoleBadgeClick && onRoleBadgeClick(role.activityRoleId)}
                    >
                      <Badge
                        overlap='circle'
                        anchorOrigin={{
                          vertical: 'bottom',
                          horizontal: 'right',
                        }}
                        badgeContent={
                          role.completed && (
                            <Box>
                              <CheckCircleIcon
                                style={{
                                  color: '#60B386',
                                  backgroundColor: '#ffffff',
                                  borderRadius: '50%',
                                  fontSize: '15px',
                                }}
                              />
                            </Box>
                          )
                        }
                      >
                        <Box width='26px' height='26px' display='flex' alignItems='center'>
                          <RoleIcon
                            iconName={role.roleIcon}
                            size={'22px'}
                            color={'#646464'}
                            style={{ marginLeft: '2px', marginRight: '4px' }}
                          />
                        </Box>
                      </Badge>
                    </Box>
                  );
                }
                if (noIcon) {
                  return (
                    <Typography style={{ fontSize: '12px', display: 'flex', alignItems: 'center' }}>
                      + {noIcon.length} other/s
                    </Typography>
                  );
                }
                return '';
              })}
            </Box>
          );
        }
        if (icons.length === 0 && noIcon.length > 0) {
          if (noIcon.length <= 3) {
            return (
              <Box
                style={{ display: 'flex', flex: '1' }}
                onClick={() => profileOpenTrigger('profile', row.userId, row.profileId)}
              >
                <Typography style={{ fontSize: '13px', display: 'flex', alignItems: 'center' }}>
                  {noIcon.map((role: RoleType, i: number) => {
                    return i + 1 === noIcon.length ? `${role.roleName}` : `${role.roleName}, `;
                  })}
                </Typography>
              </Box>
            );
          }
          return (
            <Box
              style={{ display: 'flex', flex: '1' }}
              onClick={() => profileOpenTrigger('profile', row.userId, row.profileId)}
            >
              <Typography style={{ fontSize: '13px', display: 'flex', alignItems: 'center' }}>
                {noIcon.length} role/s
              </Typography>
            </Box>
          );
        }
        if (row.roles.length === 0) {
          return (
            <Box onClick={() => profileOpenTrigger('profile', row.userId, row.profileId)} style={{ cursor: 'pointer' }}>
              <Typography noWrap style={{ fontSize: '13px', color: '#646464' }}>
                No roles
              </Typography>
            </Box>
          );
        }
        return 'Error';
      },
    },
    {
      field: 'program',
      headerName: 'Program(s)',
      width: 200,
      disableColumnMenu: true,
      renderHeader: () => {
        return <Typography style={{ fontWeight: 600, fontSize: '14px' }}>Program(s)</Typography>;
      },
      renderCell: ({ row }: any) => {
        if (row.programs.length === 1) {
          return (
            <Box onClick={() => profileOpenTrigger('program', row.userId, row.profileId)} style={{ cursor: 'pointer' }}>
              <Typography noWrap style={{ fontSize: '13px' }}>
                {row.programs[0].programName}
              </Typography>
            </Box>
          );
        }
        if (row.programs.length === 2) {
          return (
            <Box onClick={() => profileOpenTrigger('program', row.userId, row.profileId)} style={{ cursor: 'pointer' }}>
              <Typography noWrap style={{ fontSize: '13px' }}>
                {row.programs[0].programName} + {row.programs.length - 1} other
              </Typography>
            </Box>
          );
        }
        if (row.programs.length > 2) {
          return (
            <Box onClick={() => profileOpenTrigger('program', row.userId, row.profileId)} style={{ cursor: 'pointer' }}>
              <Typography noWrap style={{ fontSize: '13px' }}>
                {row.programs[0].programName} + {row.programs.length - 1} others
              </Typography>
            </Box>
          );
        }
        return (
          <Box onClick={() => profileOpenTrigger('activity', row.userId, row.profileId)} style={{ cursor: 'pointer' }}>
            <Typography noWrap style={{ fontSize: '13px', color: '#646464' }}>
              No enrolments
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'activities',
      headerName: 'Activities',
      width: 200,
      disableColumnMenu: true,
      renderHeader: () => {
        return <Typography style={{ fontWeight: 600, fontSize: '14px' }}>Activities</Typography>;
      },
      renderCell: ({ row }: any) => {
        if (row.activities.length === 1 && row.activities[0].name.length > 25) {
          return (
            <Box
              onClick={() => profileOpenTrigger('activity', row.userId, row.profileId)}
              style={{ cursor: 'pointer' }}
            >
              <Typography noWrap style={{ fontSize: '13px' }}>
                {row.activities.length} activity
              </Typography>
            </Box>
          );
        }
        if (row.activities.length > 1 && row.activities[0].name.length > 20) {
          return (
            <Box
              onClick={() => profileOpenTrigger('activity', row.userId, row.profileId)}
              style={{ cursor: 'pointer' }}
            >
              <Typography noWrap style={{ fontSize: '13px' }}>
                {row.activities.length} activities
              </Typography>
            </Box>
          );
        }
        if (row.activities.length === 1) {
          return (
            <Box
              onClick={() => profileOpenTrigger('activity', row.userId, row.profileId)}
              style={{ cursor: 'pointer' }}
            >
              <Typography noWrap style={{ fontSize: '13px' }}>
                {row.activities[0].name}
              </Typography>
            </Box>
          );
        }
        if (row.activities.length === 2) {
          return (
            <Box
              onClick={() => profileOpenTrigger('activity', row.userId, row.profileId)}
              style={{ cursor: 'pointer' }}
            >
              <Typography noWrap style={{ fontSize: '13px' }}>
                {row.activities[0].name} + {row.activities.length - 1} other
              </Typography>
            </Box>
          );
        }
        if (row.activities.length > 2) {
          return (
            <Box
              onClick={() => profileOpenTrigger('activity', row.userId, row.profileId)}
              style={{ cursor: 'pointer' }}
            >
              <Typography noWrap style={{ fontSize: '13px' }}>
                {row.activities[0].name} + {row.activities.length - 1} others
              </Typography>
            </Box>
          );
        }
        return (
          <Box onClick={() => profileOpenTrigger('activity', row.userId, row.profileId)} style={{ cursor: 'pointer' }}>
            <Typography noWrap style={{ fontSize: '13px', color: '#646464' }}>
              No enrolments
            </Typography>
          </Box>
        );
      },
    },
    // Optional columns
    ...(optionalColumns.dateOfBirth
      ? [
          {
            field: 'dob',
            headerName: 'Date of Birth',
            width: 120,
            disableColumnMenu: true,
            hideSortIcons: true,
            sortable: false,
            renderHeader: () => {
              return (
                <Box
                  onClick={() => {
                    setOrder('dob');
                  }}
                  style={{ display: 'flex', alignItems: 'center', gap: '3px' }}
                >
                  <Typography style={{ fontWeight: 600, fontSize: '14px' }}>DOB</Typography>
                  {orderBy === 'dob' ? (
                    orderDirection ? (
                      <ArrowUpward style={{ fontSize: '18px', color: '#464646' }} />
                    ) : (
                      <ArrowDownward style={{ fontSize: '18px', color: '#464646' }} />
                    )
                  ) : null}
                </Box>
              );
            },
            renderCell: (params: GridCellParams) => {
              const { row } = params;
              if (!row.dob) {
                return <Typography style={{ fontSize: '13px' }}>-</Typography>;
              }
              const dobDate = DateTime.fromJSDate(new Date(Date.parse(row.dob)));
              return <Typography style={{ fontSize: '13px' }}>{dobDate.toFormat('d MMM yyyy')}</Typography>;
            },
          },
        ]
      : []),
    ...(optionalColumns.description
      ? [
          {
            field: 'description',
            headerName: 'Description',
            flex: 1,
            disableColumnMenu: true,
            hideSortIcons: true,
            sortable: false,
            renderHeader: () => {
              return <Typography style={{ fontWeight: 600, fontSize: '14px' }}>Description</Typography>;
            },
            renderCell: (params: GridCellParams) => {
              const { row } = params;
              const plainText = descriptionToPlainText(row.description);
              return (
                <Typography noWrap style={{ fontSize: '13px' }}>
                  {plainText || '-'}
                </Typography>
              );
            },
          },
        ]
      : []),
    ...(optionalColumns.volunteerHours
      ? [
          {
            field: 'volunteerHours',
            headerName: 'Hours',
            width: 90,
            disableColumnMenu: true,
            hideSortIcons: true,
            sortable: false,
            renderHeader: () => {
              return (
                <Box
                  onClick={() => {
                    setOrder('hours');
                  }}
                  style={{ display: 'flex', alignItems: 'center', gap: '3px' }}
                >
                  <Typography style={{ fontWeight: 600, fontSize: '14px' }}>Hours</Typography>
                  {orderBy === 'hours' ? (
                    orderDirection ? (
                      <ArrowUpward style={{ fontSize: '18px', color: '#464646' }} />
                    ) : (
                      <ArrowDownward style={{ fontSize: '18px', color: '#464646' }} />
                    )
                  ) : null}
                </Box>
              );
            },
            renderCell: (params: GridCellParams) => {
              const { row } = params;
              const hours = row.volunteerHours || 0;
              return <Typography style={{ fontSize: '13px' }}>{hours.toFixed(1)}</Typography>;
            },
          },
        ]
      : []),
    ...(optionalColumns.lastAttended
      ? [
          {
            field: 'lastAttended',
            headerName: 'Last Attended',
            width: 120,
            disableColumnMenu: true,
            hideSortIcons: true,
            sortable: false,
            renderHeader: () => {
              return (
                <Box
                  onClick={() => {
                    setOrder('lastAttended');
                  }}
                  style={{ display: 'flex', alignItems: 'center', gap: '3px' }}
                >
                  <Typography style={{ fontWeight: 600, fontSize: '14px' }}>Last Attended</Typography>
                  {orderBy === 'lastAttended' ? (
                    orderDirection ? (
                      <ArrowUpward style={{ fontSize: '18px', color: '#464646' }} />
                    ) : (
                      <ArrowDownward style={{ fontSize: '18px', color: '#464646' }} />
                    )
                  ) : null}
                </Box>
              );
            },
            renderCell: (params: GridCellParams) => {
              const { row } = params;
              if (!row.lastAttended || !row.lastAttended.date) {
                return <Typography style={{ fontSize: '13px' }}>-</Typography>;
              }
              const lastDate = DateTime.fromJSDate(new Date(Date.parse(row.lastAttended.date)));
              return <Typography style={{ fontSize: '13px' }}>{lastDate.toFormat('d MMM yyyy')}</Typography>;
            },
          },
        ]
      : []),
  ];

  const [urlParam, setUrlParam] = useQueryParam<string>('view');

  return (
    <>
      <Box
        style={{
          alignSelf: 'flex-end',
          paddingRight: '10px',
          paddingTop: '10px',
          display: 'flex',
          gap: '10px',
          alignItems: 'center',
        }}
      >
        {/* Download Button */}
        <Button
          onClick={handleDownloadButtonClick}
          startIcon={<GetApp style={{ fontSize: '14px' }} />}
          style={{
            backgroundColor: 'transparent',
            color: '#646464',
            border: '1px solid #d9d9d9',
            borderRadius: '4px',
            padding: '4.5px 12px',
            fontSize: '12px',
            fontWeight: 600,
            textTransform: 'none',
            minWidth: 'auto',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          Exports
        </Button>

        {/* Columns Dropdown */}
        <TooltipMenu
          open={openColumns}
          onClose={() => {
            setOpenColumns(false);
          }}
          title={
            <Box
              display='flex'
              flexDirection='column'
              alignItems='flex-start'
              style={{ padding: '5px 10px', justifyContent: 'flex-start' }}
            >
              {/* Optional columns - user can toggle - shown at top */}
              <FormControlLabel
                className={classes.edit}
                control={
                  <Checkbox
                    style={{ color: '#fff' }}
                    checked={optionalColumns.dateOfBirth}
                    onChange={() => toggleOptionalColumn('dateOfBirth')}
                  />
                }
                label='Date of Birth'
                style={{ color: optionalColumns.dateOfBirth ? 'rgba(255, 255, 255, 0.7)' : undefined }}
              />
              <FormControlLabel
                className={classes.edit}
                control={
                  <Checkbox
                    style={{ color: '#fff' }}
                    checked={optionalColumns.description}
                    onChange={() => toggleOptionalColumn('description')}
                  />
                }
                label='Description'
                style={{ color: optionalColumns.description ? 'rgba(255, 255, 255, 0.7)' : undefined }}
              />
              <FormControlLabel
                className={classes.edit}
                control={
                  <Checkbox
                    style={{ color: '#fff' }}
                    checked={optionalColumns.volunteerHours}
                    onChange={() => toggleOptionalColumn('volunteerHours')}
                  />
                }
                label='Volunteer Hours'
                style={{ color: optionalColumns.volunteerHours ? 'rgba(255, 255, 255, 0.7)' : undefined }}
              />
              <FormControlLabel
                className={classes.edit}
                control={
                  <Checkbox
                    style={{ color: '#fff' }}
                    checked={optionalColumns.lastAttended}
                    onChange={() => toggleOptionalColumn('lastAttended')}
                  />
                }
                label='Last Attended'
                style={{ color: optionalColumns.lastAttended ? 'rgba(255, 255, 255, 0.7)' : undefined }}
              />
              {/* Divider */}
              <Box my={1} style={{ borderTop: '1px solid rgba(255,255,255,0.2)', width: '100%' }} />
              {/* Base columns - always visible and disabled */}
              <FormControlLabel
                className={classes.disabledLabel}
                control={<Checkbox checked disabled />}
                label='Name'
              />
              <FormControlLabel
                className={classes.disabledLabel}
                control={<Checkbox checked disabled />}
                label='Email'
              />
              <FormControlLabel
                className={classes.disabledLabel}
                control={<Checkbox checked disabled />}
                label='Phone'
              />
              <FormControlLabel
                className={classes.disabledLabel}
                control={<Checkbox checked disabled />}
                label='Status'
              />
              <FormControlLabel
                className={classes.disabledLabel}
                control={<Checkbox checked disabled />}
                label='Created'
              />
              <FormControlLabel
                className={classes.disabledLabel}
                control={<Checkbox checked disabled />}
                label='Role(s)'
              />
              <FormControlLabel
                className={classes.disabledLabel}
                control={<Checkbox checked disabled />}
                label='Program(s)'
              />
              <FormControlLabel
                className={classes.disabledLabel}
                control={<Checkbox checked disabled />}
                label='Activities'
              />
            </Box>
          }
        >
          <Button
            startIcon={<ViewColumn style={{ fontSize: '14px' }} />}
            endIcon={<ExpandMore style={{ fontSize: '14px' }} />}
            onClick={() => {
              setOpenColumns(true);
            }}
            style={{
              backgroundColor: 'transparent',
              color: '#646464',
              border: '1px solid #d9d9d9',
              borderRadius: '4px',
              padding: '4.5px 12px',
              fontSize: '12px',
              fontWeight: 600,
              textTransform: 'none',
              minWidth: 'auto',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            Columns
          </Button>
        </TooltipMenu>

        {/* Tools Dropdown */}
        <TooltipMenu
          open={openTools}
          onClose={() => {
            setOpenTools(false);
          }}
          title={
            <Box
              display='flex'
              flexDirection='column'
              alignItems='flex-start'
              style={{ padding: '5px 10px', justifyContent: 'flex-start' }}
            >
              <Button
                startIcon={<LocationOn style={{ fontSize: '16px' }} />}
                onClick={handleLocationSearchClick}
                style={{
                  color: '#fff',
                  textTransform: 'none',
                  fontSize: '14px',
                  fontWeight: 400,
                  padding: '4px 8px',
                  justifyContent: 'flex-start',
                  width: '100%',
                }}
              >
                Location Search
              </Button>
            </Box>
          }
        >
          <Button
            startIcon={<Build style={{ fontSize: '14px' }} />}
            endIcon={<ExpandMore style={{ fontSize: '14px' }} />}
            onClick={() => {
              setOpenTools(true);
            }}
            style={{
              backgroundColor: 'transparent',
              color: '#646464',
              border: '1px solid #d9d9d9',
              borderRadius: '4px',
              padding: '4.5px 12px',
              fontSize: '12px',
              fontWeight: 600,
              textTransform: 'none',
              minWidth: 'auto',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            Tools
          </Button>
        </TooltipMenu>

        {/* View Toggle */}
        <ToggleButtonGroup
          value={urlParam ?? 'simple'}
          exclusive
          // eslint-disable-next-line
          onChange={(e, value) => {
            setUrlParam(value);
          }}
          aria-label='text alignment'
        >
          <ToggleButton
            value='simple'
            aria-label='left aligned'
            style={
              urlParam === 'simple' || urlParam === undefined
                ? { backgroundColor: '#7047EB', color: '#fff', padding: '6px 10px' }
                : { padding: '6px 10px' }
            }
          >
            <Typography style={{ fontWeight: 600, fontSize: '11px' }}>Simple</Typography>
            {/* <Chip style={{ backgroundColor: '#7047EB', color: '#fff', fontSize: '8px', borderRadius: '4px', padding: '1px', height: 'auto', marginLeft: '5px' }} label="Beta" /> */}
          </ToggleButton>
          <ToggleButton
            value='advanced'
            aria-label='centered'
            style={
              urlParam === 'advanced'
                ? { backgroundColor: '#7047EB', color: '#fff', padding: '6px 10px' }
                : { padding: '6px 10px' }
            }
          >
            <Typography style={{ fontWeight: 600, fontSize: '11px' }}>Advanced</Typography>
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
      <Box position='relative' display='flex' flexDirection='column' height={1} overflow='auto' bgcolor='#fffffff'>
        <Box
          style={{
            width: '50%',
            marginBottom: '40px',
            marginTop: '20px',
            marginLeft: 'auto',
            marginRight: 'auto',
          }}
        >
          <Box style={{ display: 'flex', justifyContent: 'center' }}>
            <InputBase
              value={filterString}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  clear();
                }
              }}
              placeholder='Search by name, email, phone number'
              onChange={(e) => {
                setFilterString(e.target.value);
              }}
              style={{
                border: '.5px solid #d9d9d9',
                borderRadius: '25px 0 0 25px',
                width: '85%',
                display: 'flex',
                padding: '6px 24px',
              }}
            />
            <Button
              style={{
                backgroundColor: '#7047EB',
                borderRadius: '0 25px 25px 0',
                width: '12%',
                display: 'flex',
                padding: '8px 10px',
              }}
              onClick={() => clear()}
            >
              <Search style={{ color: '#fff' }} />
            </Button>
          </Box>
          <Box style={{ display: 'flex', justifyContent: 'center', paddingTop: '12px' }}>
            {/* Volunteers */}
            <TooltipMenu
              open={openVolunteers}
              onClose={() => {
                setOpenVolunteers(false);
              }}
              title={
                <RadioGroup
                  aria-label='includeInactive'
                  name='includeInactive'
                  value={includeInactive}
                  onChange={(e) => {
                    setIncludeInactive(e.target.value);
                    clear();
                  }}
                >
                  <Box
                    display='flex'
                    flexDirection='column'
                    alignItems='flex-start'
                    style={{ padding: '5px 10px', justifyContent: 'flex-start' }}
                  >
                    <FormControlLabel
                      className={classes.edit}
                      value='active'
                      control={<Radio style={{ color: '#fff' }} />}
                      label='Active'
                    />
                    <FormControlLabel
                      className={classes.edit}
                      value='inactive'
                      control={<Radio style={{ color: '#fff' }} />}
                      label='Inactive'
                    />
                    <FormControlLabel
                      className={classes.edit}
                      value='all'
                      control={<Radio style={{ color: '#fff' }} />}
                      label='All'
                    />
                  </Box>
                </RadioGroup>
              }
            >
              <TabletButton
                size='small'
                aria-controls='options-menu'
                aria-haspopup='true'
                onClick={() => {
                  setOpenVolunteers(true);
                }}
                endIcon={<ExpandMore />}
                style={{
                  height: '100%',
                  border: 'none',
                  boxSizing: 'border-box',
                  padding: '2px 7px',
                  marginRight: '10px',
                  color: theme.color.grey.neutral300,
                }}
              >
                <strong>{capitalize(includeInactive)}</strong>&nbsp;Volunteers
              </TabletButton>
            </TooltipMenu>
            {/* Programs */}
            <TooltipMenu
              open={openPrograms}
              onClose={() => {
                setOpenPrograms(false);
              }}
              title={
                <Box
                  display='flex'
                  flexDirection='column'
                  alignItems='flex-start'
                  style={{ padding: '5px 10px', justifyContent: 'flex-start' }}
                >
                  {filteredProgramIds?.map((program) => {
                    return (
                      <FormControlLabel
                        className={classes.edit}
                        control={<Checkbox style={{ color: '#fff' }} />}
                        label={program.name}
                        name={program.programId}
                        checked={programFilter && programFilter.includes(program.programId)}
                        onClick={() => programFilterChange(program.programId)}
                      />
                    );
                  })}
                </Box>
              }
            >
              <TabletButton
                size='small'
                aria-controls='options-menu'
                aria-haspopup='true'
                onClick={() => {
                  setOpenPrograms(true);
                }}
                endIcon={<ExpandMore />}
                style={{
                  height: '100%',
                  border: 'none',
                  boxSizing: 'border-box',
                  padding: '2px 7px',
                  marginRight: '10px',
                  color: theme.color.grey.neutral300,
                }}
              >
                <strong>{programFilter.length === 0 ? 'All' : programFilter.length}</strong>&nbsp;Programs
              </TabletButton>
            </TooltipMenu>
            {/* Activities */}
            <TooltipMenu
              open={openActivities}
              onClose={() => {
                setOpenActivities(false);
              }}
              title={
                <Box
                  display='flex'
                  flexDirection='column'
                  alignItems='flex-start'
                  style={{ padding: '5px 10px', justifyContent: 'flex-start' }}
                >
                  {activityFilterOptions?.map((activity) => {
                    return (
                      <FormControlLabel
                        className={classes.edit}
                        control={<Checkbox style={{ color: '#fff' }} />}
                        label={activity.name}
                        name={activity.activityId}
                        checked={activityFilter && activityFilter.includes(activity.activityId)}
                        onClick={() => activityFilterChange(activity.activityId)}
                      />
                    );
                  })}
                </Box>
              }
            >
              <TabletButton
                size='small'
                aria-controls='options-menu'
                aria-haspopup='true'
                onClick={() => {
                  setOpenActivities(true);
                }}
                endIcon={<ExpandMore />}
                style={{
                  height: '100%',
                  border: 'none',
                  boxSizing: 'border-box',
                  padding: '2px 7px',
                  marginRight: '10px',
                  color: theme.color.grey.neutral300,
                }}
              >
                <strong>{activityFilter.length === 0 ? 'All' : activityFilter.length}</strong>&nbsp;Activities
              </TabletButton>
            </TooltipMenu>
            {/* Roles */}
            <TooltipMenu
              open={openRoles}
              onClose={() => {
                setOpenRoles(false);
              }}
              title={
                <Box
                  display='flex'
                  flexDirection='column'
                  alignItems='flex-start'
                  style={{ padding: '5px 10px', justifyContent: 'flex-start' }}
                >
                  {activityRoles?.map((role) => {
                    return (
                      <FormControlLabel
                        className={classes.edit}
                        control={<Checkbox style={{ color: '#fff' }} />}
                        label={
                          <Box>
                            <RoleIcon style={{ height: '12px', paddingRight: '3px' }} iconName={role.icon ?? ''} />{' '}
                            {role.name}
                          </Box>
                        }
                        name={role.activityRoleId}
                        checked={roleFilter && roleFilter.includes(role.activityRoleId)}
                        onClick={() => roleFilterChange(role.activityRoleId)}
                      />
                    );
                  })}
                </Box>
              }
            >
              <TabletButton
                size='small'
                aria-controls='options-menu'
                aria-haspopup='true'
                onClick={() => {
                  setOpenRoles(true);
                }}
                endIcon={<ExpandMore />}
                style={{
                  height: '100%',
                  border: 'none',
                  boxSizing: 'border-box',
                  padding: '2px 7px',
                  marginRight: '10px',
                  color: theme.color.grey.neutral300,
                }}
              >
                <strong>{roleFilter.length === 0 ? 'All' : roleFilter.length}</strong>&nbsp;Roles
              </TabletButton>
            </TooltipMenu>
            {locationFilter && (
              <div>
                <TabletButton
                  size='small'
                  onMouseEnter={() => setLocationChipHovered(true)}
                  onMouseLeave={() => setLocationChipHovered(false)}
                  onClick={() => {
                    setLocationFilter(null);
                    setOrderBy('name');
                    setOrderDirection(true);
                    clear();
                  }}
                  startIcon={locationChipHovered ? <Close /> : <LocationOn />}
                  style={{
                    height: '100%',
                    border: 'none',
                    boxSizing: 'border-box',
                    padding: '2px 10px',
                    marginRight: '10px',
                    backgroundColor: '#7047EB',
                    color: 'white',
                  }}
                >
                  <strong>Location</strong>
                </TabletButton>
              </div>
            )}
            <TabletButton
              size='small'
              aria-controls='options-menu'
              aria-haspopup='true'
              onClick={() => {
                reset();
              }}
              startIcon={<Refresh />}
              style={{
                height: '100%',
                border: 'none',
                boxSizing: 'border-box',
                padding: '2px 7px',
                marginRight: '10px',
                color: theme.color.grey.neutral300,
              }}
            >
              <strong>Reset</strong>
            </TabletButton>
          </Box>
        </Box>
        <Box
          style={{ width: '98%', marginLeft: 'auto', marginRight: 'auto', overflowX: 'auto', flex: 1, minHeight: 0 }}
        >
          <DataGrid
            columns={columns}
            pagination
            onPageSizeChange={(pageSize) => updateLimit(pageSize)}
            pageSize={limit}
            rows={volunteerDatabase?.volunteers ?? []}
            loading={loading}
            page={currentPage}
            rowsPerPageOptions={[25]}
            disableSelectionOnClick
            rowCount={volunteerDatabase?.total}
            rowHeight={70}
            headerHeight={30}
            onPageChange={(e) => {
              changePage(e);
            }}
            components={{
              NoRowsOverlay: () => (
                <Box
                  height='100%'
                  width='100%'
                  alignItems='center'
                  justifyContent='center'
                  display={'flex'}
                  flexDirection='column'
                  style={{ color: '#464646' }}
                >
                  {error ? (
                    <>
                      <Warning style={{ fontSize: '48px', color: '#ff9800', marginBottom: '16px' }} />
                      <Typography variant='h6' style={{ marginBottom: '8px', color: '#ff9800' }}>
                        Warning
                      </Typography>
                      <Typography variant='body2' style={{ textAlign: 'center', maxWidth: '400px' }}>
                        {error}
                      </Typography>
                      <Button
                        onClick={() => fetch()}
                        style={{ marginTop: '16px', textTransform: 'none' }}
                        variant='outlined'
                        size='small'
                      >
                        Try Again
                      </Button>
                    </>
                  ) : (
                    <>
                      <span role='img' aria-label="Can't find" style={{ fontSize: '24px', marginBottom: '8px' }}>
                        😕
                      </span>
                      No results found
                    </>
                  )}
                </Box>
              ),
              NoResultsOverlay: () => (
                <Box
                  height='100%'
                  width='100%'
                  alignItems='center'
                  justifyContent='center'
                  display={'flex'}
                  style={{ color: '#464646' }}
                >
                  <span role='img' aria-label="Can't find">
                    😕&nbsp;&nbsp;&nbsp;
                  </span>
                  No results found
                </Box>
              ),
            }}
            className={classes.hideRightSeparator}
            paginationMode='server'
            style={{
              fontSize: '14px',
              border: 'none',
            }}
          />
        </Box>

        {profileOpen && openProfileId && openUserId && (
          <VolunteerCommonProfileDialog
            key={openProfileId}
            profileId={openProfileId}
            userId={openUserId}
            open={profileOpen}
            openOn={openProfilePage}
            onClose={() => setProfileOpen(false)}
          />
        )}
      </Box>

      {/* Exports Popover */}
      <Popover
        open={Boolean(exportsPopoverAnchor)}
        anchorEl={exportsPopoverAnchor}
        onClose={handleExportsPopoverClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          style: {
            maxHeight: 300,
            width: 600,
            padding: '8px',
          },
        }}
      >
        <ExportsList exportType='volunteer' onCreate={handleCsvDownloadRequest} />
      </Popover>

      {/* Location Search Dialog */}
      <LocationSearchDialog
        open={locationSearchOpen}
        onClose={() => setLocationSearchOpen(false)}
        onSearch={handleLocationSearch}
      />
    </>
  );
});

export default VolunteerDatabaseScreenSimple;
